/**
 * Schema Factory Functions
 * 
 * Factory functions to generate Schema.org structured data for Finanzly pages.
 * These create type-safe, reusable schemas that can be easily customized per page.
 * 
 * Architecture:
 * - Base schemas (Organization, WebSite) used site-wide
 * - Page-specific schemas (SoftwareApplication for tools, Article for content)
 * - Dynamic schemas (BreadcrumbList based on navigation)
 * 
 * Usage:
 * ```tsx
 * import { createCalculatorSchema } from '@/lib/schema-factory';
 * 
 * const schema = createCalculatorSchema({
 *   name: 'Compound Interest Calculator',
 *   description: '...',
 *   url: '/tools/compound-interest',
 * });
 * ```
 */

import type {
  OrganizationSchema,
  WebSiteSchema,
  SoftwareApplicationSchema,
  FAQPageSchema,
  HowToSchema,
  BreadcrumbListSchema,
  ArticleSchema,
  Question,
  HowToStep,
} from '@/types/schema';

/**
 * Base URL for the website
 * Should match your canonical domain
 */
const BASE_URL = 'https://finanzly.es';

/**
 * Organization Schema
 * Represents Finanzly as a company
 * Used site-wide to establish brand identity
 */
export function createOrganizationSchema(): OrganizationSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${BASE_URL}/#organization`,
    name: 'Finanzly',
    url: BASE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/logo.svg`,
      width: 200,
      height: 50,
    },
    description: 'Free financial calculators and tools to help you make informed financial decisions.',
    sameAs: [
      // Add social media profiles when available
      // 'https://twitter.com/finanzly',
      // 'https://www.facebook.com/finanzly',
      // 'https://www.linkedin.com/company/finanzly',
    ],
  };
}

/**
 * WebSite Schema
 * Main website information with search functionality
 * Used in the main layout
 */
export function createWebSiteSchema(): WebSiteSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${BASE_URL}/#website`,
    name: 'Finanzly',
    url: BASE_URL,
    description: 'Free financial calculators, tools, and educational resources for personal finance management.',
    publisher: createOrganizationSchema(),
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
    inLanguage: ['en', 'es', 'fr', 'de', 'hi'],
  };
}

/**
 * SoftwareApplication Schema for Calculators
 * Critical for SEO of calculator pages
 * Helps Google understand these are free tools
 */
interface CalculatorSchemaOptions {
  name: string;
  description: string;
  url: string;
  features?: string[];
  category?: 'FinanceApplication' | 'CalculatorApplication' | 'BusinessApplication';
  screenshot?: string;
}

export function createCalculatorSchema(options: CalculatorSchemaOptions): SoftwareApplicationSchema {
  const fullUrl = options.url.startsWith('http') ? options.url : `${BASE_URL}${options.url}`;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: options.name,
    description: options.description,
    url: fullUrl,
    applicationCategory: options.category || 'FinanceApplication',
    operatingSystem: 'Web Browser',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    featureList: options.features || [
      'Free to use',
      'No registration required',
      'Instant calculations',
      'Mobile friendly',
    ],
    screenshot: options.screenshot,
    author: createOrganizationSchema(),
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
  };
}

/**
 * FAQPage Schema for Articles
 * Great for "People also ask" boxes in Google
 */
interface FAQSchemaOptions {
  questions: Array<{
    question: string;
    answer: string;
  }>;
}

export function createFAQSchema(options: FAQSchemaOptions): FAQPageSchema {
  const mainEntity: Question[] = options.questions.map(q => ({
    '@context': 'https://schema.org',
    '@type': 'Question',
    name: q.question,
    acceptedAnswer: {
      '@context': 'https://schema.org',
      '@type': 'Answer',
      text: q.answer,
    },
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };
}

/**
 * HowTo Schema for Step-by-Step Guides
 * Useful for tutorial articles
 */
interface HowToSchemaOptions {
  name: string;
  description: string;
  steps: Array<{
    name?: string;
    text: string;
  }>;
  totalTime?: string; // e.g., "PT10M" for 10 minutes
  image?: string;
}

export function createHowToSchema(options: HowToSchemaOptions): HowToSchema {
  const steps: HowToStep[] = options.steps.map((step, index) => ({
    '@context': 'https://schema.org',
    '@type': 'HowToStep',
    position: index + 1,
    name: step.name,
    text: step.text,
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: options.name,
    description: options.description,
    step: steps,
    totalTime: options.totalTime,
    image: options.image,
  };
}

/**
 * BreadcrumbList Schema
 * Helps Google understand site hierarchy
 */
interface BreadcrumbItem {
  name: string;
  url?: string;
}

export function createBreadcrumbSchema(items: BreadcrumbItem[]): BreadcrumbListSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@context': 'https://schema.org',
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url ? (item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`) : undefined,
    })),
  };
}

/**
 * Article Schema for Blog Posts and Educational Content
 */
interface ArticleSchemaOptions {
  headline: string;
  description: string;
  url: string;
  image?: string | string[];
  datePublished: string;
  dateModified?: string;
  authorName?: string;
  keywords?: string[];
}

export function createArticleSchema(options: ArticleSchemaOptions): ArticleSchema {
  const fullUrl = options.url.startsWith('http') ? options.url : `${BASE_URL}${options.url}`;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: options.headline,
    description: options.description,
    image: options.image,
    datePublished: options.datePublished,
    dateModified: options.dateModified || options.datePublished,
    author: {
      '@type': 'Person',
      name: options.authorName || 'Finanzly Team',
    },
    publisher: createOrganizationSchema(),
    mainEntityOfPage: fullUrl,
    keywords: options.keywords,
  };
}

/**
 * Helper: Generate breadcrumbs from route path
 * Automatically creates breadcrumb schema from URL structure
 * 
 * Example:
 * /en/tools/compound-interest-calculator
 * → Home > Tools > Compound Interest Calculator
 */
export function generateBreadcrumbsFromPath(
  path: string,
  labels?: Record<string, string>
): BreadcrumbListSchema {
  const segments = path.split('/').filter(Boolean);
  const items: BreadcrumbItem[] = [{ name: 'Home', url: '/' }];

  let currentPath = '';
  for (const segment of segments) {
    // Skip language segment
    if (['en', 'es', 'fr', 'de', 'hi'].includes(segment)) {
      continue;
    }

    currentPath += `/${segment}`;
    const name = labels?.[segment] || segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    
    items.push({
      name,
      url: currentPath,
    });
  }

  return createBreadcrumbSchema(items);
}

/**
 * Pre-defined schemas for common calculators
 * These can be imported and used directly
 */
export const CALCULATOR_SCHEMAS = {
  compoundInterest: () => createCalculatorSchema({
    name: 'Compound Interest Calculator',
    description: 'Free compound interest calculator to calculate how your investments grow over time. See the power of compound interest with annual vs monthly comparisons and SP500 examples.',
    url: '/tools/compound-interest-calculator',
    features: [
      'Calculate compound interest with different frequencies',
      'Compare annual vs monthly compounding',
      'Visualize investment growth with charts',
      'Free to use, no registration required',
    ],
  }),
  
  salary: () => createCalculatorSchema({
    name: 'Net Salary Calculator',
    description: 'Calculate your take-home pay after taxes and deductions. Free salary calculator for multiple countries with detailed breakdowns.',
    url: '/tools/salary-calculator',
    features: [
      'Multi-country support',
      'Detailed tax breakdown',
      'Social security calculations',
      'Monthly and annual views',
    ],
  }),
  
  retirement: () => createCalculatorSchema({
    name: 'Retirement Calculator',
    description: 'Plan your retirement with our free calculator. Calculate how much you need to save for a comfortable retirement.',
    url: '/tools/retirement-calculator',
    features: [
      'Retirement planning calculator',
      'Inflation-adjusted projections',
      'Multiple scenarios',
      'Visual retirement planning',
    ],
  }),
  
  mortgage: () => createCalculatorSchema({
    name: 'Mortgage Calculator',
    description: 'Free mortgage calculator to estimate your monthly payments, total interest, and amortization schedule.',
    url: '/tools/mortgage-calculator',
    features: [
      'Monthly payment calculation',
      'Amortization schedule',
      'Interest vs principal breakdown',
      'Early payoff scenarios',
    ],
  }),
  
  investment: () => createCalculatorSchema({
    name: 'Investment Calculator',
    description: 'Calculate potential investment returns with our free investment calculator. Compare different investment scenarios.',
    url: '/tools/investment-calculator',
    features: [
      'ROI calculations',
      'Multiple investment scenarios',
      'Risk-adjusted returns',
      'Visual investment projections',
    ],
  }),
  
  budget: () => createCalculatorSchema({
    name: 'Budget Calculator',
    description: 'Free budget calculator to help you manage your personal finances. Track income, expenses, and savings goals.',
    url: '/tools/budget-calculator',
    features: [
      'Income and expense tracking',
      'Savings goals',
      'Budget recommendations',
      'Visual budget breakdown',
    ],
  }),
  
  monthlySavings: () => createCalculatorSchema({
    name: 'Monthly Savings Calculator',
    description: 'Calculate how much you need to save monthly to reach your financial goals. Free savings calculator with goal tracking.',
    url: '/tools/monthly-savings-calculator',
    features: [
      'Goal-based savings planning',
      'Monthly contribution calculator',
      'Visual progress tracking',
      'Multiple savings goals support',
    ],
  }),
};
